/**
 * Retail > OMNI Performance. The native Cockpit rebuild of the QuickSight
 * "OMNI Performance Recap" daily email (spec: 00_omni-performance-reporting.md).
 *
 * /retail/performance — the daily fleet report: a single flat list of every
 * store (ordered by store number), followed by a fleet-wide grand-total row that
 * stands out (bold, shaded `amp-*` tint, top border). No region/district grouping
 * (per Franco: list all stores, leave regions and districts out). Five sections
 * (Sales, Transactions, Traffic & Conversion, Units, DPT & UPT), each shown across
 * Yesterday / Week-to-date / Month-to-date / Year-to-date periods. For every
 * metric the grid shows this year, the same weekday last year (`LY`, date - 364d)
 * and the comp between them, colored via compColorClass. The Traffic & Conversion
 * section notes that conversion counts only days with traffic data and shows
 * that day count next to each conversion figure. A section switcher chooses
 * which of the five sections is on screen; a date picker chooses the as-of day;
 * an Excel export mirrors the other retail exports.
 *
 * The same-calendar-date columns and the Plan / Bgt % columns were removed on
 * 2026-08-12, and year-to-date added, at William's and Franco's direction.
 *
 * RBAC: fleet view requires retail.omni.read (the tool's own capability,
 * distinct from the Fleet Map's retail.scorecard.read); a store user with
 * stores:store gets an own-store view. Scope is enforced server-side, so the
 * page renders whatever the API returns.
 *
 * Resilience: the backend endpoint (api Task 1) ships in parallel, so the page
 * is wrapped in an ErrorBoundary, reads every field defensively, and renders a
 * quiet "not available yet" state on any 404 / 503 / shape mismatch instead of
 * crashing — the same posture as the other tools built before their backend.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Check, Download, Loader2, Mail, MailCheck, TriangleAlert } from 'lucide-react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import { FilterLabel } from '@/components/FilterLabel';
import { gridTheadClassName, InfoTooltip } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { compColorClass } from '@/lib/compColor';
import { FOUR_FIVE_FOUR_CALENDAR_ARTICLE_URL, FOUR_FIVE_FOUR_CALENDAR_TOOLTIP } from '@/lib/helpLinks';
import { useSession } from '@/lib/useSession';
import { formatComputedAt, formatWeekDate } from '@/lib/useRetailStores';
import {
  useOmni,
  useOmniSubscription,
  downloadOmniXlsx,
  flattenOmni,
  blockFor,
  formatOmniValue,
  formatInt,
  omniGroupTooltip,
  omniLyColumnDate,
  omniLyColumnTooltip,
  OMNI_SECTIONS,
  OMNI_PERIODS,
  OMNI_SECTION_COLUMNS,
  type OmniSectionKey,
  type OmniDisplayRow,
  type OmniWindows,
} from '@/lib/useOmni';

// ---------------------------------------------------------------------------
// RBAC
// ---------------------------------------------------------------------------

/**
 * Fleet users hold stores.performance (the tool's own accessSlug); a store
 * manager holds stores.sweetspot and gets an own-store view. Either grants
 * access; the backend enforces which stores actually come back.
 */
function canViewOmni(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  if (!perms || perms.length === 0) return false;
  return perms.includes('stores.performance') || perms.includes('stores.sweetspot');
}

// ---------------------------------------------------------------------------
// Date helper
// ---------------------------------------------------------------------------

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Yesterday (local), the latest complete report day. */
function defaultAsOf(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toIsoDate(d);
}

// ---------------------------------------------------------------------------
// Presentational atoms
// ---------------------------------------------------------------------------

function AccessDeniedCard() {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-8 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">
        OMNI Performance is gated by the retail.omni.read capability.
      </p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">Ask IT to grant you access.</p>
    </div>
  );
}

function NotAvailableCard() {
  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-8 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="text-sm font-medium text-amp-ink dark:text-amp-dark-ink">Not available yet.</p>
      <p className="mt-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        The OMNI report is being built. It will populate here once the daily metrics feed is live.
      </p>
    </div>
  );
}


function SectionSwitcher({
  value,
  onChange,
}: {
  value: OmniSectionKey;
  onChange: (k: OmniSectionKey) => void;
}) {
  return (
    <div>
      <FilterLabel as="div">Section</FilterLabel>
      <div className="flex flex-wrap gap-1.5">
        {OMNI_SECTIONS.map((s) => (
          <Pill
            key={s.key}
            variant={s.key === value ? 'selected' : 'outline'}
            aria-pressed={s.key === value}
            title={s.blurb}
            onClick={() => onChange(s.key)}
          >
            {s.label}
          </Pill>
        ))}
      </div>
    </div>
  );
}

/**
 * Daily-email subscription control for this report. Reads the caller's current
 * state from GET /v1/retail/omni/subscription and flips it via PUT. A subscribed
 * caller sees an Unsubscribe action; an unsubscribed one sees "Email me this
 * report daily". A Store Manager who is auto-enrolled is shown an "On by default"
 * badge and can still turn it off (an unsubscribe writes status unsubscribed).
 *
 * The control hides itself entirely when the subscription endpoint is not
 * available yet (404 / 503 / shape mismatch), matching the page's degrade-
 * gracefully posture while the api backend ships in parallel.
 */
function SubscriptionControl({ canView }: { canView: boolean }) {
  const { subscription, available, save, isSaving } = useOmniSubscription(canView);
  const [feedback, setFeedback] = useState<{ kind: 'saved' | 'error'; msg: string } | null>(null);

  // Clear the transient saved/error note after a few seconds.
  useEffect(() => {
    if (!feedback) return;
    const t = window.setTimeout(() => setFeedback(null), 4000);
    return () => window.clearTimeout(t);
  }, [feedback]);

  // Guard after the hooks so hook order stays stable across renders.
  if (!canView || !available || !subscription) return null;

  const subscribed = subscription.subscribed;

  const handleToggle = () => {
    if (isSaving) return;
    const next = !subscribed;
    save(next, {
      onSuccess: () => setFeedback({ kind: 'saved', msg: next ? 'Subscribed' : 'Unsubscribed' }),
      onError: (e) => setFeedback({ kind: 'error', msg: e instanceof Error ? e.message : 'Update failed' }),
    });
  };

  const Icon = isSaving ? Loader2 : subscribed ? MailCheck : Mail;
  const buttonLabel = subscribed ? 'Unsubscribe' : 'Email me this report daily';
  const buttonTitle = subscribed
    ? 'You get this report by email every day. Click to stop.'
    : 'Get this report by email every day.';

  return (
    <div className="shrink-0 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amp-line bg-amp-white px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="flex items-center gap-2">
        <span className="text-sm text-amp-ink dark:text-amp-dark-ink">
          {subscribed
            ? 'You get this report by email every day.'
            : 'Get this report by email every day.'}
        </span>
        {subscription.auto_enrolled && (
          <Pill variant="outline" title="Store managers are subscribed by default. You can turn it off.">
            On by default
          </Pill>
        )}
      </div>
      <div className="ml-auto flex items-center gap-2">
        {feedback && (
          <span
            role="status"
            className={
              feedback.kind === 'error'
                ? 'text-[12px] font-medium text-red-700 dark:text-red-300'
                : 'inline-flex items-center gap-1 text-[12px] font-medium text-amp-olive dark:text-amp-olive-dark'
            }
          >
            {feedback.kind === 'saved' && <Check className="h-3 w-3" aria-hidden />}
            {feedback.msg}
          </span>
        )}
        <button
          type="button"
          onClick={handleToggle}
          disabled={isSaving}
          title={buttonTitle}
          className="inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          <Icon className={`h-3 w-3 ${isSaving ? 'animate-spin' : ''}`} aria-hidden />
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grid
// ---------------------------------------------------------------------------

/** Left border that separates one period group from the next. */
const GROUP_DIVIDER = 'border-l border-amp-line dark:border-amp-dark-line';

/**
 * Row tint + weight per kind. The grand-total row is bold with an amp-* background
 * tint and a top border so it stands out from the flat list of store rows.
 */
function rowClasses(kind: OmniDisplayRow['kind']): string {
  if (kind === 'total') {
    return 'border-t-2 border-amp-olive dark:border-amp-dark-line bg-amp-olive-soft/70 dark:bg-amp-dark-line/70 font-bold';
  }
  return 'hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm';
}

// ---------------------------------------------------------------------------
// Mobile cards
// ---------------------------------------------------------------------------

/**
 * One store as a card, for phones.
 *
 * The desktop grid is one row per store and thirteen cells wide (a Store cell
 * plus three measures across four periods). Stacked down a phone that is thirteen
 * labelled lines, most of a screen per store, which is what Steven saw and
 * rightly called unreadable.
 *
 * The card TRANSPOSES it instead of hiding anything: measures become rows and
 * the four periods become columns, so Sales reads as three short lines rather
 * than twelve, and the comparison the report exists for (this period against the
 * same period last year) is read across a single line instead of by scrolling.
 * Every value the desktop grid shows is still here,
 * through the same `formatOmniValue` and `compColorClass` the table cells use,
 * so a card and a row can never disagree.
 *
 * The grand-total row keeps its weight and tint, so the fleet line still reads
 * differently from a store.
 */
function OmniCard({ row, section }: { row: OmniDisplayRow; section: OmniSectionKey }) {
  const cols = OMNI_SECTION_COLUMNS[section];
  const isTotal = row.kind === 'total';
  const place = [row.city, row.state].filter(Boolean).join(', ');

  return (
    <li
      className={`px-3 py-3 ${
        isTotal
          ? 'border-t-2 border-amp-olive bg-amp-olive-soft/70 font-semibold dark:border-amp-dark-line dark:bg-amp-dark-line/70'
          : ''
      }`}
    >
      <p className="mb-2 text-[14px] font-semibold text-amp-ink dark:text-amp-dark-ink">
        {row.label}
        {row.sublabel && (
          <span className="ml-1 font-normal text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {row.sublabel}
          </span>
        )}
        {place && (
          <span className="ml-1 font-normal text-amp-ink-muted dark:text-amp-dark-ink-muted">
            &middot; {place}
          </span>
        )}
      </p>

      {/* Measures down, periods across. `grid-cols-[auto_repeat(4,1fr)]` keeps the
          measure names as narrow as their text and splits the rest evenly, so the
          four period columns line up down the whole card. */}
      <dl className="grid grid-cols-[auto_repeat(4,minmax(0,1fr))] gap-x-2 gap-y-1 text-[12px]">
        <dt aria-hidden />
        {OMNI_PERIODS.map((p) => (
          <dd
            key={`head-${p.key}`}
            className="text-right text-[11px] font-medium text-amp-ink-muted dark:text-amp-dark-ink-muted"
          >
            {/* "Week to date" would wrap in a third of a phone; the periods are
                fixed and few, so the short forms are spelled out rather than
                truncated, with the full name on the title for anyone unsure. */}
            <span title={p.label}>{OMNI_PERIOD_SHORT[p.key]}</span>
          </dd>
        ))}

        {cols.map((c) => (
          <Fragment key={`row-${c.field}-${c.label}`}>
            <dt className="whitespace-nowrap pr-1 text-amp-ink-muted dark:text-amp-dark-ink-muted">
              {c.label}
            </dt>
            {OMNI_PERIODS.map((p) => {
              const block = blockFor(row.sections, section, p.key);
              const raw = block ? block[c.field] : null;
              const value = typeof raw === 'number' ? raw : null;
              const colorCls =
                c.kind === 'comp' ? compColorClass(value) : 'text-amp-ink dark:text-amp-dark-ink';
              const convDaysRaw =
                section === 'traffic' && c.field === 'conv_ty' && block ? block['conv_days'] : null;
              const convDays = typeof convDaysRaw === 'number' ? convDaysRaw : null;
              return (
                <dd
                  key={`${row.key}-${p.key}-${c.field}-${c.label}`}
                  className={`text-right tabular-nums ${colorCls}`}
                >
                  {formatOmniValue(value, c.kind)}
                  {convDays != null && (
                    <span className="ml-0.5 font-normal text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      ({formatInt(convDays)}d)
                    </span>
                  )}
                </dd>
              );
            })}
          </Fragment>
        ))}
      </dl>
    </li>
  );
}

/** Short period headings, because a quarter of a phone will not hold "Week to date". */
const OMNI_PERIOD_SHORT: Record<string, string> = {
  yesterday: 'Yest',
  wtd: 'WTD',
  mtd: 'MTD',
  ytd: 'YTD',
};

/** Loading / error / empty copy in card mode, where there is no table row to fill. */
function OmniCardNotice({ message }: { message: string }) {
  return (
    <p className="px-3 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">{message}</p>
  );
}

function OmniCards({
  rows,
  section,
  isLoading,
  error,
}: {
  rows: OmniDisplayRow[];
  section: OmniSectionKey;
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) return <OmniCardNotice message="Loading..." />;
  if (error) return <OmniCardNotice message="Not available yet." />;
  if (rows.length === 0) return <OmniCardNotice message="No stores to show for this day." />;
  return (
    <ul className="divide-y divide-amp-line dark:divide-amp-dark-line">
      {rows.map((row) => (
        <OmniCard key={row.key} row={row} section={section} />
      ))}
    </ul>
  );
}

/** The one last-year field that carries a header date line: the same-weekday
 * basis. Every other column (TY, the comps, Conv %) shows no date. */
function isLyField(field: string): boolean {
  return field === 'ly_week';
}

function OmniTable({
  rows,
  section,
  isLoading,
  error,
  windows,
}: {
  rows: OmniDisplayRow[];
  section: OmniSectionKey;
  isLoading: boolean;
  error: Error | null;
  windows: OmniWindows | null;
}) {
  const cols = OMNI_SECTION_COLUMNS[section];
  const totalCols = 3 + OMNI_PERIODS.length * cols.length;

  return (
    <table className="min-w-full text-sm">
      <thead className={gridTheadClassName}>
        <tr>
          <th rowSpan={2} className="px-2 py-2 text-left align-bottom font-medium whitespace-nowrap">
            Store
          </th>
          <th rowSpan={2} className="px-2 py-2 text-left align-bottom font-medium whitespace-nowrap">
            City
          </th>
          <th rowSpan={2} className="px-2 py-2 text-left align-bottom font-medium whitespace-nowrap">
            State
          </th>
          {OMNI_PERIODS.map((p) => (
            <th
              key={p.key}
              colSpan={cols.length}
              title={omniGroupTooltip(p.key, windows) ?? undefined}
              className={`px-2 py-2 text-center font-semibold ${GROUP_DIVIDER}`}
            >
              {p.label}
            </th>
          ))}
        </tr>
        <tr>
          {OMNI_PERIODS.map((p) =>
            cols.map((c, i) => {
              const isLy = isLyField(c.field);
              const dateLine = isLy ? omniLyColumnDate(windows, p.key) : null;
              const dateTooltip = isLy ? omniLyColumnTooltip(windows, p.key) : null;
              // A column's own acronym tooltip (c.title, e.g. "This Year (TY)")
              // only applies to columns with no LY date, so the two never compete.
              const tooltip = dateTooltip ?? c.title ?? undefined;
              return (
                <th
                  key={`${p.key}-${c.field}-${c.label}`}
                  title={tooltip}
                  className={`px-2 py-1.5 text-right font-medium whitespace-nowrap ${i === 0 ? GROUP_DIVIDER : ''}`}
                >
                  {c.label}
                  {dateLine && (
                    <span className="block text-[10px] font-normal normal-case text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      {dateLine}
                    </span>
                  )}
                </th>
              );
            }),
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
        {isLoading ? (
          <tr>
            <td colSpan={totalCols} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Loading...
            </td>
          </tr>
        ) : error ? (
          <tr>
            <td colSpan={totalCols} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Not available yet.
            </td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={totalCols} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
              No stores to show for this day.
            </td>
          </tr>
        ) : (
          rows.map((row) => {
            return (
              <tr key={row.key} className={rowClasses(row.kind)}>
                <td className="px-2 py-2 whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">
                  {row.label}
                  {row.sublabel && (
                    <span className="ml-1 font-normal text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      {row.sublabel}
                    </span>
                  )}
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">{row.city ?? ''}</td>
                <td className="px-2 py-2 whitespace-nowrap text-amp-ink dark:text-amp-dark-ink">{row.state ?? ''}</td>
                {OMNI_PERIODS.map((p) => {
                  const block = blockFor(row.sections, section, p.key);
                  return cols.map((c, i) => {
                    const raw = block ? block[c.field] : null;
                    const value = typeof raw === 'number' ? raw : null;
                    const colorCls =
                      c.kind === 'comp'
                        ? compColorClass(value)
                        : 'text-amp-ink dark:text-amp-dark-ink';
                    // Days-with-traffic count sits next to the conversion figure
                    // (Conv % column, traffic section only) to flag partial coverage.
                    const convDaysRaw =
                      section === 'traffic' && c.field === 'conv_ty' && block ? block['conv_days'] : null;
                    const convDays = typeof convDaysRaw === 'number' ? convDaysRaw : null;
                    return (
                      <td
                        key={`${row.key}-${p.key}-${c.field}-${c.label}`}
                        className={`px-2 py-2 text-right whitespace-nowrap ${colorCls} ${i === 0 ? GROUP_DIVIDER : ''}`}
                      >
                        {formatOmniValue(value, c.kind)}
                        {convDays != null && (
                          <span className="ml-1 font-normal text-amp-ink-muted dark:text-amp-dark-ink-muted">
                            ({formatInt(convDays)} {convDays === 1 ? 'day' : 'days'})
                          </span>
                        )}
                      </td>
                    );
                  });
                })}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function RetailPerformanceInner() {
  const { user } = useSession();
  const perms = user?.groups ?? [];
  const canView = canViewOmni(perms, user?.superAdmin);

  const [asOfInput, setAsOfInput] = useState<string>(defaultAsOf);
  const [section, setSection] = useState<OmniSectionKey>('sales');
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const { asOf, computedAt, freshness, windows, stores, grandTotal, isLoading, error } = useOmni(
    canView ? asOfInput : null,
  );

  const rows = useMemo(() => flattenOmni(stores, grandTotal), [stores, grandTotal]);
  const storeCount = useMemo(() => rows.filter((r) => r.kind === 'store').length, [rows]);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadOmniXlsx(asOfInput);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  if (!user && !isLoading) return null; // AuthGate handles unauthenticated state

  if (user && !canView) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">OMNI Performance</h1>
        <AccessDeniedCard />
      </div>
    );
  }

  const hasData = rows.length > 0;
  const showNotAvailable = !isLoading && !hasData;

  return (
    <div className="h-full flex flex-col min-h-0">

      <div className="flex flex-wrap items-end justify-between mb-4 gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Store</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">OMNI Performance</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Daily fleet report by store: sales, transactions, traffic, units, and Dollars Per
            Transaction (DPT) / Units Per Transaction (UPT), each against the same weekday
            last year.
            {asOf ? <> Report day {formatWeekDate(asOf)}.</> : ''}
            {computedAt ? <> Computed {formatComputedAt(computedAt)}.</> : ''}
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-end gap-2">
          <div>
            {/* Rendered as a div, not a <label>: the tooltip is a button, and a
                button nested inside a label steals the label's click target. */}
            <FilterLabel as="div">
              As of
              <InfoTooltip label="the 4-5-4 calendar" learnMoreHref={FOUR_FIVE_FOUR_CALENDAR_ARTICLE_URL}>
                {FOUR_FIVE_FOUR_CALENDAR_TOOLTIP}
              </InfoTooltip>
            </FilterLabel>
            <input
              id="omni-as-of"
              type="date"
              aria-label="As of"
              value={asOfInput}
              max={toIsoDate(new Date())}
              onChange={(e) => setAsOfInput(e.target.value)}
              className="border border-amp-line rounded px-2 py-1.5 text-xs bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
            />
          </div>
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={exporting}
            title="Download this day's OMNI report as Excel"
            className="inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Export
          </button>
          <ToolPinToggle toolSlug="performance" toolName="OMNI Performance" />
        </div>
      </div>

      <SubscriptionControl canView={canView} />

      {/* Data-freshness warning. The API only accepts the newest business day when
          at least half the active physical stores have actually reported for it, so
          a report that runs between the two loaders that feed retail_sales_daily
          cannot quietly show every store as zero. `note` is null on a clean day. */}
      {freshness?.note && (
        <div
          className="shrink-0 mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100"
          role="status"
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{freshness.note}</span>
        </div>
      )}

      {toast && (
        <div
          className="shrink-0 mb-4 flex items-center justify-between rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200"
          role="status"
        >
          {toast}
          <button type="button" onClick={() => setToast(null)} className="ml-4 text-xs">
            &times;
          </button>
        </div>
      )}

      {showNotAvailable ? (
        <NotAvailableCard />
      ) : (
        <DataGridShell
          total={storeCount}
          countNoun="stores"
          toolbar={
            <div>
              <SectionSwitcher value={section} onChange={setSection} />
              {section === 'traffic' && (
                <p className="mt-2 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Conversion counts only the days that had traffic data. The number of covered days
                  appears in parentheses next to each conversion figure. Compare periods that share the
                  same coverage.
                </p>
              )}
            </div>
          }
          renderCards={() => (
            <OmniCards rows={rows} section={section} isLoading={isLoading} error={error} />
          )}
        >
          <OmniTable rows={rows} section={section} isLoading={isLoading} error={error} windows={windows} />
        </DataGridShell>
      )}
    </div>
  );
}

export function RetailPerformance() {
  return (
    <ErrorBoundary>
      <RetailPerformanceInner />
    </ErrorBoundary>
  );
}
